/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import Loader from 'components/atoms/loader';
import AiManagementDrawer from 'components/molecules/ai_management_drawer';
import { useProposalShell } from 'components/organisms/ai_management_bidding_proposal/context';
import { useProposalItems } from 'hooks/useProposalItems';
import {
  ProposalCompetitivePrice,
  ProposalItemColumnResponse,
  ProposalItemColumnType,
  ProposalItemResponse,
} from 'services/actions/ai_management_proposal';
import { proposalItemTarget } from 'services/ai_management_proposal';
import { Container } from './styles';

interface AiManagementProposalItemsProps {
  biddingId: number;
}

const COLUMN_TYPE_LABEL: Record<ProposalItemColumnType, string> = {
  texto: 'Texto',
  numero: 'Número',
  moeda: 'Moeda (R$)',
  data: 'Data',
};

const money = (value: number | null | undefined) =>
  typeof value === 'number'
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const mesAno = (data: string | null | undefined): string => {
  if (!data) return '—';
  const parsed = new Date(data);
  if (Number.isNaN(parsed.getTime())) return '—';
  const mes = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  return `${mes}/${parsed.getUTCFullYear()}`;
};

const toNumberOrNull = (value: string): number | null => {
  const normalized = value.replace(/\./g, '').replace(',', '.').trim();
  if (normalized === '') return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const keyOf = (item: ProposalItemResponse) => `${item.numero_lote ?? ''}#${item.numero_item ?? ''}`;

const itemDoAlvo = (target: string | null) => {
  const match = target?.match(/^item-(\d*)-(\d+)$/);
  if (!match) return null;
  return { lote: match[1] ? Number(match[1]) : null, item: Number(match[2]) };
};

const AiManagementProposalItems: React.FC<AiManagementProposalItemsProps> = ({ biddingId }) => {
  const { trackSave, setPendencies, focusTarget, stepActions } = useProposalShell();
  const [goTo] = useState(() => itemDoAlvo(focusTarget));
  const {
    data,
    loading,
    error,
    cellErrors,
    query,
    undoToast,
    spreadsheetSummary,
    spreadsheetError,
    spreadsheetBusy,
    competitivePrice,
    setFilters,
    applyChange,
    selectAll,
    addColumn,
    updateColumn,
    removeColumn,
    addLine,
    removeLine,
    deselectWithUndo,
    dismissUndo,
    downloadSpreadsheet,
    importSpreadsheet,
    dismissSpreadsheetSummary,
    dismissSpreadsheetError,
    openCompetitivePrice,
    aplicarPrecoCompetitivo,
  } = useProposalItems(biddingId, { trackSave, goTo });

  const [painelAberto, setPainelAberto] = useState<ProposalItemResponse | null>(null);
  const [precoDigitado, setPrecoDigitado] = useState('');

  const [menuColuna, setMenuColuna] = useState<string | null>(null);
  const [novaColuna, setNovaColuna] = useState<{
    nome: string;
    tipo: ProposalItemColumnType;
  } | null>(null);
  const [renomeando, setRenomeando] = useState<{ id: string; nome: string } | null>(null);
  const [excluirExigida, setExcluirExigida] = useState<ProposalItemColumnResponse | null>(null);
  const [novaLinha, setNovaLinha] = useState<{
    descricao: string;
    unidade: string;
    quantidade: string;
    numero_lote: string;
  } | null>(null);
  const [formErro, setFormErro] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [arquivoPendente, setArquivoPendente] = useState<File | null>(null);
  const [formatoInvalidoLocal, setFormatoInvalidoLocal] = useState(false);

  const resumo = data?.resumo;
  const itens = data?.itens ?? [];
  const paginacao = data?.paginacao;
  const porLote = data?.julgamento === 'lote';
  const colunas = data?.colunas ?? [];
  const colunasCliente = colunas.filter((c) => c.origem === 'cliente' && !c.excluida);
  // 10 colunas fixas + as do cliente + a de ações.
  const totalCols = 10 + colunasCliente.length + 1;

  const exigidas = colunas.filter((c) => c.exigida_pelo_edital && !c.excluida).map((c) => c.chave);
  const isExigida = (chave: string) => exigidas.includes(chave);

  const pageAllSelected = itens.length > 0 && itens.every((i) => i.selecionado);

  // Pendências: itens selecionados sem preço, com coluna exigida vazia ou com preço acima das casas
  // decimais da regra.
  useEffect(() => {
    if (!resumo) return;
    const faltas = itens.filter((item) => {
      if (!item.selecionado) return false;
      const semPreco = item.preco_unitario == null;
      const exigidoVazio = ['marca', 'modelo', 'fabricante'].some(
        (chave) => isExigida(chave) && !(item as unknown as Record<string, unknown>)[chave],
      );
      return semPreco || exigidoVazio;
    }).length;
    // Sem nenhum item selecionado, conta como 1 pendência (a etapa precisa de ao menos um item).
    const total = resumo.itens_selecionados === 0 ? 1 : faltas + cellErrors.size;
    setPendencies('itens', total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumo, itens, cellErrors, setPendencies]);

  const onEdit = (
    item: ProposalItemResponse,
    campo: 'marca' | 'modelo' | 'fabricante',
    value: string,
  ) =>
    applyChange({ numero_lote: item.numero_lote, numero_item: item.numero_item, [campo]: value });

  const onEditPrice = (item: ProposalItemResponse, value: string) => {
    // Casas decimais são validadas pelo back (422 → erro na célula), com o número oficial da regra.
    applyChange({
      numero_lote: item.numero_lote,
      numero_item: item.numero_item,
      preco_unitario: toNumberOrNull(value),
    });
  };

  const onToggleItem = (item: ProposalItemResponse) => {
    if (porLote) return;
    if (item.selecionado) {
      deselectWithUndo(item.numero_lote, item.numero_item, false);
    } else {
      applyChange({
        numero_lote: item.numero_lote,
        numero_item: item.numero_item,
        selecionado: true,
      });
    }
  };

  const onVerPrecoCompetitivo = (item: ProposalItemResponse) => {
    setPainelAberto(item);
    // Semeia com vírgula decimal: toNumberOrNull trata ponto como milhar, então "23.5" viraria 235.
    setPrecoDigitado(
      item.preco_unitario != null ? String(item.preco_unitario).replace('.', ',') : '',
    );
    openCompetitivePrice(item);
  };

  const fecharPainelCompetitivo = () => {
    setPainelAberto(null);
    setPrecoDigitado('');
  };

  const onAplicarPrecoCompetitivo = () => {
    if (!painelAberto) return;
    aplicarPrecoCompetitivo(
      painelAberto,
      painelAberto.preco_unitario,
      toNumberOrNull(precoDigitado),
    );
    fecharPainelCompetitivo();
  };

  const loteSelecionado = (numeroLote: number | null) =>
    (data?.lotes ?? []).find((l) => l.numero_lote === numeroLote)?.selecionado ?? false;

  const submitNovaColuna = () => {
    if (!novaColuna || !novaColuna.nome.trim()) return;
    addColumn(novaColuna.nome.trim(), novaColuna.tipo).then((err) => {
      if (err) setFormErro(err);
      else {
        setNovaColuna(null);
        setFormErro('');
      }
    });
  };

  const submitRenomear = () => {
    if (!renomeando || !renomeando.nome.trim()) return;
    updateColumn(renomeando.id, { nome: renomeando.nome.trim() }).then((err) => {
      if (err) setFormErro(err);
      else {
        setRenomeando(null);
        setFormErro('');
      }
    });
  };

  const onExcluirColuna = (coluna: ProposalItemColumnResponse) => {
    if (coluna.exigida_pelo_edital) setExcluirExigida(coluna);
    else removeColumn(coluna.id, coluna.nome);
  };

  const confirmarExcluirExigida = () => {
    if (excluirExigida) removeColumn(excluirExigida.id, excluirExigida.nome);
    setExcluirExigida(null);
  };

  const submitNovaLinha = () => {
    if (!novaLinha || !novaLinha.descricao.trim()) return;
    addLine({
      descricao: novaLinha.descricao.trim(),
      unidade: novaLinha.unidade.trim() || 'un',
      quantidade: novaLinha.quantidade.trim() === '' ? 1 : Number(novaLinha.quantidade),
      numero_lote: novaLinha.numero_lote.trim() === '' ? null : Number(novaLinha.numero_lote),
    }).then((err) => {
      if (err) setFormErro(err);
      else {
        setNovaLinha(null);
        setFormErro('');
      }
    });
  };

  const onEditValor = (item: ProposalItemResponse, idColuna: string, value: string) =>
    applyChange({
      numero_lote: item.numero_lote,
      numero_item: item.numero_item,
      valores: { ...item.valores, [idColuna]: value },
    });

  const dispararImportacao = (file: File) => {
    importSpreadsheet(file);
    setArquivoPendente(null);
  };

  // O input é limpo antes (via key) para permitir escolher o mesmo arquivo de novo após um erro.
  const onClickImportar = () => {
    fileInputRef.current?.click();
  };

  // Com itens selecionados, pede confirmação antes de importar; sem itens, importa direto.
  const onArquivoEscolhido = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    // Evento sintético do React: lê o file antes de qualquer async.
    event.target.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setFormatoInvalidoLocal(true);
      return;
    }
    if (resumo && resumo.itens_selecionados > 0) {
      setArquivoPendente(file);
    } else {
      dispararImportacao(file);
    }
  };

  const confirmarImportacao = () => {
    if (arquivoPendente) dispararImportacao(arquivoPendente);
  };

  const cancelarImportacao = () => setArquivoPendente(null);

  const renderCounter = () => {
    if (!resumo) return null;
    const prefixoLote = porLote
      ? `${resumo.lotes_selecionados} de ${resumo.total_lotes} lotes · `
      : '';
    return (
      <span className="selected-counter">
        {prefixoLote}
        {resumo.itens_selecionados} de {resumo.total_itens} itens selecionados
      </span>
    );
  };

  // Planilha ao lado do título da etapa, como no protótipo.
  const renderSheetActions = () => (
    <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        className="d-none"
        onChange={onArquivoEscolhido}
      />
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
        disabled={spreadsheetBusy}
        onClick={downloadSpreadsheet}
      >
        <i className="material-icons-outlined mr-1" aria-hidden="true" style={{ fontSize: 18 }}>
          file_download
        </i>
        Baixar modelo de planilha
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center"
        title="Envie a planilha modelo preenchida com os preços e dados da sua proposta."
        disabled={spreadsheetBusy}
        onClick={onClickImportar}
      >
        <i className="material-icons-outlined mr-1" aria-hidden="true" style={{ fontSize: 18 }}>
          file_upload
        </i>
        Importar planilha preenchida
      </button>
    </div>
  );

  const temLotes = (data?.lotes ?? []).some((l) => l.numero_lote !== null);

  const renderToolbar = () => (
    <div className={`table-tools${temLotes ? ' with-lot-filter' : ''}`}>
      <label className="item-search mb-0" htmlFor="proposal-items-search">
        Buscar itens
        <div>
          <i className="material-icons-outlined" aria-hidden="true">
            search
          </i>
          <input
            id="proposal-items-search"
            type="search"
            placeholder="Ex.: caneta, toner, 15"
            defaultValue={query.q ?? ''}
            onChange={(event) => {
              const { value } = event.target;
              setFilters({ q: value, page: 1 });
            }}
          />
        </div>
      </label>
      {temLotes && (
        <label className="lot-filter mb-0" htmlFor="proposal-items-lote">
          Lote
          <select
            id="proposal-items-lote"
            value={query.lote ?? ''}
            onChange={(event) => {
              const { value } = event.target;
              setFilters({ lote: value === '' ? undefined : value, page: 1 });
            }}
          >
            <option value="">Todos os lotes</option>
            {(data?.lotes ?? [])
              .filter((l) => l.numero_lote !== null)
              .map((l) => (
                <option key={l.numero_lote ?? ''} value={l.numero_lote ?? ''}>
                  Lote {l.numero_lote}
                </option>
              ))}
          </select>
        </label>
      )}
      <label className="selected-filter mb-0" htmlFor="proposal-items-only-selected">
        <input
          id="proposal-items-only-selected"
          type="checkbox"
          checked={!!query.selecionados}
          onChange={(event) => {
            const { checked } = event.target;
            setFilters({ selecionados: checked || undefined, page: 1 });
          }}
        />
        Mostrar só selecionados{resumo ? ` (${resumo.itens_selecionados})` : ''}
      </label>
      <div className="d-flex align-items-center justify-content-end">
        {renderCounter()}
        {resumo && resumo.itens_selecionados > 0 && (
          <button
            type="button"
            className="btn btn-sm btn-link p-0 ml-2 text-nowrap proposal-items-clear"
            onClick={() => selectAll(false)}
          >
            Limpar seleção
          </button>
        )}
      </div>
    </div>
  );

  const renderSelectAllBanner = () => {
    // No julgamento por lote a seleção é feita pela linha de lote, não pela faixa.
    if (porLote || !resumo || itens.length === 0) return null;
    const todosSelecionados =
      resumo.itens_selecionados >= resumo.total_itens && resumo.total_itens > 0;

    if (todosSelecionados && resumo.total_itens > itens.length) {
      return (
        <div className="proposal-items-banner alert alert-success py-2 d-flex align-items-center justify-content-between">
          <span>Todos os {resumo.total_itens} itens da licitação estão selecionados.</span>
          <button
            type="button"
            className="btn btn-sm btn-link p-0"
            onClick={() => selectAll(false)}
          >
            Limpar seleção
          </button>
        </div>
      );
    }

    if (pageAllSelected && resumo.itens_selecionados < resumo.total_itens) {
      return (
        <div className="proposal-items-banner alert alert-info py-2 d-flex align-items-center justify-content-between">
          <span>Os {itens.length} itens desta página estão selecionados.</span>
          <button type="button" className="btn btn-sm btn-link p-0" onClick={() => selectAll(true)}>
            Selecionar todos os {resumo.total_itens} itens
          </button>
        </div>
      );
    }

    return null;
  };

  // Renomear/Excluir ficam só nas colunas criadas pelo cliente.
  const headerLabel = (chave: string, texto: string, calculada = false) => {
    const coluna = colunas.find((c) => c.chave === chave);
    const nome = coluna?.nome ?? texto;
    return (
      <span className="header-label">
        {nome}
        {isExigida(chave) && (
          <i className="material-icons-outlined header-icon" title="Exigida pelo edital">
            gavel
          </i>
        )}
        {calculada && (
          <i
            className="material-icons-outlined header-icon"
            title="Coluna calculada pela plataforma"
          >
            lock
          </i>
        )}
      </span>
    );
  };

  const renderTextCell = (item: ProposalItemResponse, campo: 'marca' | 'modelo' | 'fabricante') => {
    const valor = item[campo];
    const obrigatorioVazio = item.selecionado && isExigida(campo) && !valor;
    return (
      <td className={obrigatorioVazio ? 'proposal-items-obrigatorio' : ''}>
        <input
          type="text"
          className="form-control form-control-sm"
          defaultValue={valor ?? ''}
          onBlur={(event) => onEdit(item, campo, event.target.value)}
        />
        {obrigatorioVazio && <small className="d-block text-danger">Obrigatório pelo edital</small>}
      </td>
    );
  };

  const renderClientCell = (item: ProposalItemResponse, coluna: ProposalItemColumnResponse) => {
    const valorKey = `${keyOf(item)}::${coluna.id}`;
    const erro = cellErrors.get(valorKey) || cellErrors.get(keyOf(item));
    const valorAtual = (item.valores && (item.valores[coluna.id] as string)) ?? '';
    const tipoInput = coluna.tipo === 'data' ? 'date' : 'text';
    return (
      <td key={coluna.id} className={erro ? 'proposal-items-obrigatorio' : ''}>
        <input
          type={tipoInput}
          inputMode={coluna.tipo === 'numero' || coluna.tipo === 'moeda' ? 'decimal' : undefined}
          className="form-control form-control-sm"
          defaultValue={valorAtual}
          onBlur={(event) => onEditValor(item, coluna.id, event.target.value)}
        />
      </td>
    );
  };

  const renderRow = (item: ProposalItemResponse) => {
    const erroPreco = cellErrors.get(keyOf(item));
    const doCliente = item.origem === 'cliente';
    const estadoPreco = competitivePrice.get(keyOf(item));
    // Depois da 1ª consulta, o botão some se não houver histórico do órgão nem do cliente.
    return (
      <tr
        key={keyOf(item)}
        className={item.selecionado ? 'selecionado selected-row' : ''}
        data-proposal-target={proposalItemTarget(item.numero_lote, item.numero_item)}
      >
        <td className="select-cell">
          <input
            type="checkbox"
            checked={item.selecionado}
            disabled={porLote}
            title={porLote ? 'Na proposta por lote, selecione o lote inteiro' : undefined}
            onChange={() => onToggleItem(item)}
          />
        </td>
        <td>
          {item.numero_item}
          {doCliente && (
            <span className="proposal-items-tag badge badge-light ml-1">Adicionada por você</span>
          )}
        </td>
        <td className="proposal-items-desc">{item.descricao}</td>
        <td>{item.unidade}</td>
        <td>{item.quantidade}</td>
        {renderTextCell(item, 'marca')}
        {renderTextCell(item, 'modelo')}
        {renderTextCell(item, 'fabricante')}
        <td className={erroPreco ? 'proposal-items-obrigatorio' : ''}>
          <span className="money-input">
            <span>R$</span>
            <input
              type="text"
              inputMode="decimal"
              className="form-control form-control-sm proposal-items-price"
              defaultValue={item.preco_unitario != null ? String(item.preco_unitario) : ''}
              placeholder="0,00"
              onBlur={(event) => onEditPrice(item, event.target.value)}
            />
          </span>
          {erroPreco && <small className="d-block text-danger">{erroPreco}</small>}
        </td>
        {colunasCliente.map((coluna) => renderClientCell(item, coluna))}
        <td className="proposal-items-total">
          {money(
            item.preco_unitario != null ? item.preco_unitario * Number(item.quantidade ?? 0) : null,
          )}
        </td>
        <td className="proposal-items-acoes">
          <button
            type="button"
            className="btn btn-sm btn-link proposal-items-preco-competitivo p-0 mr-2"
            disabled={!!estadoPreco?.loading}
            onClick={() => onVerPrecoCompetitivo(item)}
          >
            <i className="material-icons-outlined proposal-items-preco-competitivo-icon mr-1">
              insights
            </i>
            {estadoPreco?.loading ? 'Analisando…' : 'Ver preço competitivo'}
          </button>
          {doCliente && item.numero_item != null && (
            <button
              type="button"
              className="btn btn-sm btn-link text-danger p-0"
              onClick={() =>
                removeLine({
                  numero_item: item.numero_item as number,
                  numero_lote: item.numero_lote,
                  descricao: item.descricao,
                  unidade: item.unidade ?? 'un',
                  quantidade: Number(item.quantidade ?? 1),
                })
              }
            >
              Excluir
            </button>
          )}
        </td>
      </tr>
    );
  };

  const renderLoteRow = (numeroLote: number | null) => {
    const lote = (data?.lotes ?? []).find((l) => l.numero_lote === numeroLote);
    if (!lote) return null;
    return (
      <tr key={`lote-${numeroLote ?? ''}`} className="proposal-items-lote-row">
        <td>
          <input
            type="checkbox"
            checked={loteSelecionado(numeroLote)}
            onChange={(event) => {
              const { checked } = event.target;
              if (checked) selectAll(true, numeroLote);
              else deselectWithUndo(numeroLote, null, true);
            }}
          />
        </td>
        <td colSpan={7}>
          <strong>Lote {numeroLote}</strong> · {lote.total_itens} itens
        </td>
        <td className="text-right">Subtotal</td>
        {colunasCliente.length > 0 && <td colSpan={colunasCliente.length} />}
        <td className="proposal-items-total">{money(lote.subtotal)}</td>
        <td />
      </tr>
    );
  };

  const renderBody = () => {
    if (itens.length === 0) {
      return (
        <tr>
          <td colSpan={totalCols} className="text-center text-muted py-3">
            Nenhum item encontrado.
          </td>
        </tr>
      );
    }
    if (!porLote) return itens.map(renderRow);

    const linhas: React.ReactNode[] = [];
    let loteAtual: number | null | undefined;
    itens.forEach((item) => {
      if (item.numero_lote !== loteAtual) {
        loteAtual = item.numero_lote;
        linhas.push(renderLoteRow(item.numero_lote));
      }
      linhas.push(renderRow(item));
    });
    return linhas;
  };

  // Primeira, última e as vizinhas da atual; "…" entre os saltos (como no protótipo).
  const paginasVisiveis = (atual: number, total: number) => {
    const paginas = Array.from(new Set([1, atual - 1, atual, atual + 1, total]))
      .filter((n) => n >= 1 && n <= total)
      .sort((a, b) => a - b);
    return paginas.map((n, index) => ({ n, salto: index > 0 && n - paginas[index - 1] > 1 }));
  };

  // Resumo da página, paginação e total da proposta numa linha só.
  const renderTableBottom = () => {
    const temPaginas = !!paginacao && paginacao.total_pages > 1;
    const inicio = paginacao ? (paginacao.page - 1) * paginacao.per_page + 1 : 0;
    const fim = paginacao ? Math.min(paginacao.page * paginacao.per_page, paginacao.total) : 0;
    return (
      <div className="table-bottom">
        <div className="pagination-summary">
          {paginacao && paginacao.total > 0 && `${inicio}–${fim} de ${paginacao.total} itens`}
        </div>
        {temPaginas && paginacao ? (
          <nav className="pagination" aria-label="Paginação dos itens">
            <button
              type="button"
              aria-label="Página anterior"
              disabled={paginacao.page <= 1}
              onClick={() => setFilters({ page: paginacao.page - 1 })}
            >
              <i className="material-icons-outlined" aria-hidden="true">
                chevron_left
              </i>
            </button>
            {paginasVisiveis(paginacao.page, paginacao.total_pages).map(({ n, salto }) => (
              <span key={n}>
                {salto && <span className="pagination-ellipsis">…</span>}
                <button
                  type="button"
                  aria-current={n === paginacao.page ? 'page' : undefined}
                  onClick={() => setFilters({ page: n })}
                >
                  {n}
                </button>
              </span>
            ))}
            <button
              type="button"
              aria-label="Próxima página"
              disabled={paginacao.page >= paginacao.total_pages}
              onClick={() => setFilters({ page: paginacao.page + 1 })}
            >
              <i className="material-icons-outlined" aria-hidden="true">
                chevron_right
              </i>
            </button>
          </nav>
        ) : (
          <span />
        )}
        <div className="proposal-total">
          {resumo && (
            <>
              <small>Total da proposta</small>
              <strong>{money(resumo.total_proposta)}</strong>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderNovaColunaForm = () =>
    novaColuna && (
      <div className="proposal-items-form card p-3 mb-3">
        <div className="d-flex flex-wrap align-items-end">
          <label className="small mb-0 mr-2" htmlFor="nova-coluna-nome">
            Nome da coluna
            <input
              id="nova-coluna-nome"
              type="text"
              className="form-control form-control-sm mt-1"
              value={novaColuna.nome}
              maxLength={60}
              onChange={(event) => {
                const { value } = event.target;
                setNovaColuna((prev) => (prev ? { ...prev, nome: value } : prev));
              }}
            />
          </label>
          <label className="small mb-0 mr-2" htmlFor="nova-coluna-tipo">
            Tipo
            <select
              id="nova-coluna-tipo"
              className="form-control form-control-sm mt-1"
              value={novaColuna.tipo}
              onChange={(event) => {
                const value = event.target.value as ProposalItemColumnType;
                setNovaColuna((prev) => (prev ? { ...prev, tipo: value } : prev));
              }}
            >
              {(Object.keys(COLUMN_TYPE_LABEL) as ProposalItemColumnType[]).map((t) => (
                <option key={t} value={t}>
                  {COLUMN_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="btn btn-sm btn-primary mr-2"
            onClick={submitNovaColuna}
            disabled={!novaColuna.nome.trim()}
          >
            Criar coluna
          </button>
          <button
            type="button"
            className="btn btn-sm btn-link"
            onClick={() => {
              setNovaColuna(null);
              setFormErro('');
            }}
          >
            Cancelar
          </button>
        </div>
        {formErro && <small className="text-danger d-block mt-1">{formErro}</small>}
      </div>
    );

  const renderRenomearForm = () =>
    renomeando && (
      <div className="proposal-items-form card p-3 mb-3">
        <div className="d-flex flex-wrap align-items-end">
          <label className="small mb-0 mr-2" htmlFor="renomear-coluna">
            Renomear coluna
            <input
              id="renomear-coluna"
              type="text"
              className="form-control form-control-sm mt-1"
              value={renomeando.nome}
              maxLength={60}
              onChange={(event) => {
                const { value } = event.target;
                setRenomeando((prev) => (prev ? { ...prev, nome: value } : prev));
              }}
            />
          </label>
          <button
            type="button"
            className="btn btn-sm btn-primary mr-2"
            onClick={submitRenomear}
            disabled={!renomeando.nome.trim()}
          >
            Salvar
          </button>
          <button
            type="button"
            className="btn btn-sm btn-link"
            onClick={() => {
              setRenomeando(null);
              setFormErro('');
            }}
          >
            Cancelar
          </button>
        </div>
        {formErro && <small className="text-danger d-block mt-1">{formErro}</small>}
      </div>
    );

  const renderNovaLinhaForm = () =>
    novaLinha && (
      <div className="proposal-items-form card p-3 mb-3">
        <p className="small text-muted mb-2">
          Use para incluir um item que não veio na tabela do edital.
        </p>
        <div className="d-flex flex-wrap align-items-end">
          <label className="small mb-0 mr-2 flex-grow-1" htmlFor="nova-linha-desc">
            Descrição
            <input
              id="nova-linha-desc"
              type="text"
              className="form-control form-control-sm mt-1"
              value={novaLinha.descricao}
              onChange={(event) => {
                const { value } = event.target;
                setNovaLinha((prev) => (prev ? { ...prev, descricao: value } : prev));
              }}
            />
          </label>
          <label className="small mb-0 mr-2" htmlFor="nova-linha-unidade">
            Unidade
            <input
              id="nova-linha-unidade"
              type="text"
              className="form-control form-control-sm mt-1 proposal-items-lote"
              value={novaLinha.unidade}
              onChange={(event) => {
                const { value } = event.target;
                setNovaLinha((prev) => (prev ? { ...prev, unidade: value } : prev));
              }}
            />
          </label>
          <label className="small mb-0 mr-2" htmlFor="nova-linha-qtd">
            Qtd.
            <input
              id="nova-linha-qtd"
              type="number"
              min="1"
              className="form-control form-control-sm mt-1 proposal-items-lote"
              value={novaLinha.quantidade}
              onChange={(event) => {
                const { value } = event.target;
                setNovaLinha((prev) => (prev ? { ...prev, quantidade: value } : prev));
              }}
            />
          </label>
          {porLote && (
            <label className="small mb-0 mr-2" htmlFor="nova-linha-lote">
              Lote
              <input
                id="nova-linha-lote"
                type="number"
                className="form-control form-control-sm mt-1 proposal-items-lote"
                value={novaLinha.numero_lote}
                onChange={(event) => {
                  const { value } = event.target;
                  setNovaLinha((prev) => (prev ? { ...prev, numero_lote: value } : prev));
                }}
              />
            </label>
          )}
          <button
            type="button"
            className="btn btn-sm btn-primary mr-2"
            onClick={submitNovaLinha}
            disabled={!novaLinha.descricao.trim()}
          >
            Adicionar
          </button>
          <button
            type="button"
            className="btn btn-sm btn-link"
            onClick={() => {
              setNovaLinha(null);
              setFormErro('');
            }}
          >
            Cancelar
          </button>
        </div>
        {formErro && <small className="text-danger d-block mt-1">{formErro}</small>}
      </div>
    );

  const renderModalExigida = () =>
    excluirExigida && (
      <div className="proposal-items-modal-backdrop">
        <div className="proposal-items-modal card p-3">
          <h6>Excluir coluna exigida pelo edital?</h6>
          <p className="small text-muted mb-3">
            A coluna &ldquo;{excluirExigida.nome}&rdquo; é exigida pelo edital. Se excluir, ela não
            aparecerá na proposta.
          </p>
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-sm btn-link mr-2"
              onClick={() => setExcluirExigida(null)}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={confirmarExcluirExigida}
            >
              Excluir mesmo assim
            </button>
          </div>
        </div>
      </div>
    );

  const renderUndoToast = () =>
    undoToast && (
      <div className="proposal-items-undo alert alert-dark d-flex align-items-center justify-content-between py-2 mt-3">
        <span>{undoToast.message}</span>
        <button
          type="button"
          className="btn btn-sm btn-link p-0"
          onClick={() => {
            undoToast.undo();
            dismissUndo();
          }}
        >
          Desfazer
        </button>
      </div>
    );

  // Só destaca o marcador visual: nenhuma correção ou sugestão de preço sai daqui.
  const classificarFaixa = (
    faixa: ProposalCompetitivePrice['faixa'],
    valor: number | null,
  ): 'competitivo' | 'intermediario' | 'acima' | null => {
    if (!faixa || valor == null) return null;
    if (valor <= faixa.competitivo_ate) return 'competitivo';
    if (valor <= faixa.intermediario_ate) return 'intermediario';
    return 'acima';
  };

  const renderPainelCompetitivo = () => {
    if (!painelAberto) return null;
    const estado = competitivePrice.get(keyOf(painelAberto));
    const resultado = estado?.data ?? null;
    const valorDigitado = toNumberOrNull(precoDigitado);
    const faixaClasse = resultado ? classificarFaixa(resultado.faixa, valorDigitado) : null;

    return (
      <AiManagementDrawer
        title="Últimas contratações"
        subtitle={`Item ${painelAberto.numero_item} · ${painelAberto.descricao}`}
        variant="side"
        onClose={fecharPainelCompetitivo}
      >
        {estado?.loading && <Loader />}
        {!estado?.loading && resultado && (
          <>
            {!resultado.orgao && !resultado.cliente && (
              <p className="proposal-items-competitivo-sem-historico text-muted mb-3">
                Sem histórico de contratações para este item.
              </p>
            )}

            {resultado.orgao && (
              <div className="proposal-items-competitivo-bloco mb-3">
                <h6 className="mb-2">Histórico do órgão</h6>
                <p className="mb-0">
                  Última licitação: {money(resultado.orgao.valor)} · {resultado.orgao.licitacao} ·{' '}
                  {mesAno(resultado.orgao.data)}
                </p>
              </div>
            )}

            {resultado.cliente && (
              <div className="proposal-items-competitivo-bloco mb-3">
                <h6 className="mb-2">Seu histórico</h6>
                <p className="mb-0">
                  Última licitação: {money(resultado.cliente.valor)} · {resultado.cliente.licitacao}{' '}
                  · {mesAno(resultado.cliente.data)}
                </p>
              </div>
            )}

            {(resultado.orgao || resultado.cliente) && (
              <p className="proposal-items-competitivo-nota small text-muted mb-3">
                Valores de contratações anteriores, sem correção.
              </p>
            )}

            {resultado.faixa && (
              <div className="proposal-items-competitivo-faixa mb-3">
                <div
                  className={`proposal-items-faixa-item ${
                    faixaClasse === 'competitivo' ? 'ativa' : ''
                  }`}
                >
                  Competitivo · até {money(resultado.faixa.competitivo_ate)}
                </div>
                <div
                  className={`proposal-items-faixa-item ${
                    faixaClasse === 'intermediario' ? 'ativa' : ''
                  }`}
                >
                  Intermediário · até {money(resultado.faixa.intermediario_ate)}
                </div>
                <div
                  className={`proposal-items-faixa-item ${faixaClasse === 'acima' ? 'ativa' : ''}`}
                >
                  Acima da faixa
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="preco-competitivo-input">
                Seu preço unitário
                <input
                  id="preco-competitivo-input"
                  type="text"
                  inputMode="decimal"
                  className="form-control mt-1"
                  value={precoDigitado}
                  onChange={(event) => setPrecoDigitado(event.target.value)}
                />
              </label>
              {cellErrors.get(keyOf(painelAberto)) && (
                <small className="d-block text-danger mt-1">
                  {cellErrors.get(keyOf(painelAberto))}
                </small>
              )}
            </div>

            <button type="button" className="btn btn-primary" onClick={onAplicarPrecoCompetitivo}>
              Aplicar preço
            </button>
          </>
        )}
        {!estado?.loading && !resultado && (
          <p className="text-muted">Não há histórico de contratações para este item.</p>
        )}
      </AiManagementDrawer>
    );
  };

  const renderModalConfirmarImportacao = () =>
    arquivoPendente && (
      <div className="proposal-items-modal-backdrop">
        <div className="proposal-items-modal card p-3">
          <h6>Substituir os itens preenchidos?</h6>
          <p className="small text-muted mb-3">
            A importação substituirá os itens preenchidos. Os preços e dados já lançados nesta
            proposta serão sobrescritos pelo conteúdo da planilha.
          </p>
          <div className="d-flex justify-content-end">
            <button type="button" className="btn btn-sm btn-link mr-2" onClick={cancelarImportacao}>
              Cancelar
            </button>
            <button type="button" className="btn btn-sm btn-danger" onClick={confirmarImportacao}>
              Importar e substituir
            </button>
          </div>
        </div>
      </div>
    );

  const renderResumoImportacao = () =>
    spreadsheetSummary && (
      <div className="proposal-items-import-resumo alert alert-info py-2 mb-3">
        <div className="d-flex align-items-start justify-content-between">
          <span>
            Planilha importada: {spreadsheetSummary.aplicadas} itens aplicados ·{' '}
            {spreadsheetSummary.ignoradas.length} linhas ignoradas.
          </span>
          <button
            type="button"
            className="btn btn-sm btn-link p-0"
            onClick={dismissSpreadsheetSummary}
          >
            Fechar
          </button>
        </div>
        {spreadsheetSummary.ignoradas.length > 0 && (
          <ul className="proposal-items-import-ignoradas small mb-0 mt-2">
            {spreadsheetSummary.ignoradas.map((linha) => (
              <li key={linha.linha}>
                Linha {linha.linha}: {linha.motivo}
              </li>
            ))}
          </ul>
        )}
      </div>
    );

  const renderModalErroImportacao = () => {
    const incompativel = spreadsheetError === 'planilha_incompativel';
    const formatoInvalido = spreadsheetError === 'formato_invalido' || formatoInvalidoLocal;
    const erroGenerico = !!spreadsheetError && !incompativel && !formatoInvalido;
    if (!incompativel && !formatoInvalido && !erroGenerico) return null;

    const fechar = () => {
      dismissSpreadsheetError();
      setFormatoInvalidoLocal(false);
    };

    const titulo = incompativel
      ? 'Planilha incompatível'
      : formatoInvalido
      ? 'Formato de arquivo inválido'
      : 'Não foi possível importar a planilha';
    const mensagem = incompativel
      ? 'Essa planilha parece ser de outra licitação. Baixe o modelo desta licitação e tente de novo.'
      : formatoInvalido
      ? 'Envie um arquivo no formato .xlsx (o modelo baixado pelo botão "Baixar modelo de planilha").'
      : 'Ocorreu um erro ao importar a planilha. Tente de novo em alguns instantes.';

    return (
      <div className="proposal-items-modal-backdrop">
        <div className="proposal-items-modal card p-3">
          <h6>{titulo}</h6>
          <p className="small text-muted mb-3">{mensagem}</p>
          <div className="d-flex justify-content-end">
            <button type="button" className="btn btn-sm btn-primary" onClick={fechar}>
              Entendi
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="alert alert-warning">Não foi possível carregar os itens da licitação.</div>
    );
  }
  if (loading && !data) return <Loader />;

  return (
    <Container className="proposal-items">
      {stepActions && createPortal(renderSheetActions(), stepActions)}
      {renderToolbar()}
      {renderResumoImportacao()}
      {renderNovaColunaForm()}
      {renderRenomearForm()}
      {renderNovaLinhaForm()}
      {renderModalExigida()}
      {renderModalConfirmarImportacao()}
      {renderModalErroImportacao()}
      {renderSelectAllBanner()}

      <div className="table-card">
        <div className="table-scroll">
          <table className="proposal-items-table">
            <thead>
              <tr>
                <th className="select-column">
                  {!porLote && (
                    <input
                      type="checkbox"
                      className="mr-2 align-middle"
                      aria-label="Selecionar todos os itens da página"
                      checked={pageAllSelected}
                      onChange={(event) => {
                        const { checked } = event.target;
                        itens.forEach((item) => {
                          if (item.selecionado !== checked) {
                            applyChange({
                              numero_lote: item.numero_lote,
                              numero_item: item.numero_item,
                              selecionado: checked,
                            });
                          }
                        });
                      }}
                    />
                  )}
                  Selecionar
                </th>
                <th>Item</th>
                <th>Descrição</th>
                <th>Unidade</th>
                <th>Qtd.</th>
                <th>{headerLabel('marca', 'Marca')}</th>
                <th>{headerLabel('modelo', 'Modelo')}</th>
                <th>{headerLabel('fabricante', 'Fabricante')}</th>
                <th>{headerLabel('valor_unitario', 'Valor unitário (R$)')}</th>
                {colunasCliente.map((coluna) => (
                  <th key={coluna.id} className="proposal-items-col-cliente">
                    <span className="header-label">{coluna.nome}</span>
                    <span className="header-action">
                      <button
                        type="button"
                        className="head-menu"
                        aria-label={`Opções da coluna ${coluna.nome}`}
                        aria-expanded={menuColuna === coluna.id}
                        onClick={() =>
                          setMenuColuna((atual) => (atual === coluna.id ? null : coluna.id))
                        }
                      >
                        <i className="material-icons-outlined" aria-hidden="true">
                          more_vert
                        </i>
                      </button>
                      {menuColuna === coluna.id && (
                        <span className="head-menu-list" role="menu">
                          <button
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setMenuColuna(null);
                              setRenomeando({ id: coluna.id, nome: coluna.nome });
                            }}
                          >
                            <i className="material-icons-outlined" aria-hidden="true">
                              edit
                            </i>
                            Renomear
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            className="text-danger"
                            onClick={() => {
                              setMenuColuna(null);
                              onExcluirColuna(coluna);
                            }}
                          >
                            <i className="material-icons-outlined" aria-hidden="true">
                              delete
                            </i>
                            Excluir
                          </button>
                        </span>
                      )}
                    </span>
                    <small className="text-muted d-block">{COLUMN_TYPE_LABEL[coluna.tipo]}</small>
                  </th>
                ))}
                <th>{headerLabel('valor_total', 'Valor total (R$)', true)}</th>
                <th className="add-column-head">
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0"
                    onClick={() => setNovaColuna({ nome: '', tipo: 'texto' })}
                  >
                    + Coluna
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>{renderBody()}</tbody>
          </table>
        </div>
        <div className="table-add-row">
          <button
            type="button"
            className="btn btn-sm btn-link p-0"
            title="Use para incluir um item que não veio na tabela do edital."
            onClick={() =>
              setNovaLinha({ descricao: '', unidade: '', quantidade: '1', numero_lote: '' })
            }
          >
            <i className="material-icons-outlined" aria-hidden="true">
              add
            </i>
            Adicionar linha
          </button>
          <span>Para incluir um item que não veio na tabela do edital.</span>
        </div>
      </div>

      {renderTableBottom()}

      {renderUndoToast()}
      {renderPainelCompetitivo()}
    </Container>
  );
};

export default AiManagementProposalItems;
